import { MAX_QUESTIONS_PER_SCREEN, type Screen } from '@floorpoll/shared';

/**
 * Stepping a question through the poll.
 *
 * Questions read as one continuous list across every screen. `-1` steps a
 * question one place earlier and `1` one place later; at a screen's edge that
 * step crosses into the neighbouring screen, which changes the question's
 * screen without changing its place in the overall reading order.
 *
 * Both functions are pure so the cap and the index maths can be pinned down
 * without rendering. The rule is stated once, in `canMoveQuestion`, and
 * `moveQuestion` delegates its refusal to it rather than restating it — so a
 * rule added there is honoured by both.
 */

/** Whether a step in `direction` leaves the screen rather than staying inside it. */
const stepLeavesScreen = (screen: Screen, questionIndex: number, direction: -1 | 1) =>
  direction === -1 ? questionIndex === 0 : questionIndex === screen.questions.length - 1;

export function canMoveQuestion(
  screens: Screen[],
  screenIndex: number,
  questionIndex: number,
  direction: -1 | 1,
): boolean {
  const screen = screens[screenIndex];
  if (!screen?.questions[questionIndex]) return false;
  if (!stepLeavesScreen(screen, questionIndex, direction)) return true;

  // Out of range at either end reads as undefined: the poll has no such screen.
  const neighbour = screens[screenIndex + direction];
  return !!neighbour && neighbour.questions.length < MAX_QUESTIONS_PER_SCREEN;
}

/**
 * Returns the *same array reference* when the step is refused, so a caller
 * handing the result straight to `setScreens` re-renders nothing.
 */
export function moveQuestion(
  screens: Screen[],
  screenIndex: number,
  questionIndex: number,
  direction: -1 | 1,
): Screen[] {
  const screen = screens[screenIndex];
  const question = screen?.questions[questionIndex];
  // Unreachable: canMoveQuestion refuses these same inputs on the next line.
  // It is here so noUncheckedIndexedAccess narrows both for the branches below.
  if (!screen || !question) return screens;
  if (!canMoveQuestion(screens, screenIndex, questionIndex, direction)) return screens;

  if (!stepLeavesScreen(screen, questionIndex, direction)) {
    const questions = [...screen.questions];
    // Remove-then-insert rather than a swap: with the question lifted out,
    // `questionIndex + direction` is the slot it lands in for both directions,
    // so neither branch needs index maths of its own.
    questions.splice(questionIndex, 1);
    questions.splice(questionIndex + direction, 0, question);
    return screens.map((s, i) => (i === screenIndex ? { ...s, questions } : s));
  }

  const target = screenIndex + direction;
  return screens.map((s, i) => {
    if (i === screenIndex) {
      return { ...s, questions: s.questions.filter((_, qi) => qi !== questionIndex) };
    }
    if (i !== target) return s;
    // Landing at the far end of the neighbour is what keeps the question's
    // place in the poll's overall reading order; only its screen changes.
    return {
      ...s,
      questions: direction === -1 ? [...s.questions, question] : [question, ...s.questions],
    };
  });
}
